import React, { useState } from "react";
import { Td, TableList } from "static/coreStyles/table.styled";
import { Avatar, Button, Modal, Switch, Typography } from "antd";
import { Theme } from "static/coreStyles/theme.styled";
import { getInitials } from "utility/helperFunctions";
import { useDispatch } from 'react-redux';
import { updateGroupDetails } from "actions/group";
import { EyeOutlined, ExclamationOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { UTCtoLocalDateTime } from "utility/dateTimeUtils";
import { setFilters as setRideFilters } from "actions/ride";

const { Text, Paragraph } = Typography;

export const GroupList = ({
    data: {
        id,
        groupId,
        name,
        foundingPlace,
        groupPicture,
        memberCount,
        totalKm,
        totalRides,
        createdRides,
        createdAt,
        isPrivate,
        isVerified,
        isPaymentEnabled,
        isLiveTrackingEnabledSingleDay,
        isLiveTrackingEnabledMultiDay,
        isDeleted,
        createdBy
    },
}) => {
    const [open, setOpen] = useState(false);
    const [verifyGroup, setVerifyGroup] = useState(isVerified);
    const [enablePayment, setEnablePayment] = useState(isPaymentEnabled);
    const [liveTrackingSingleDay, setLiveTrackingSingleDay] = useState(isLiveTrackingEnabledSingleDay);
    const [liveTrackingMultiDay, setLiveTrackingMultiDay] = useState(isLiveTrackingEnabledMultiDay);
    const [navigating, setNavigating] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();


    if (!id) return null;

    const handleClickOpen = () => {
        setVerifyGroup(isVerified);
        setEnablePayment(isPaymentEnabled);
        setLiveTrackingSingleDay(isLiveTrackingEnabledSingleDay);
        setLiveTrackingMultiDay(isLiveTrackingEnabledMultiDay);
        setOpen(true);
    };

    const handleClose = () => {
        setVerifyGroup(isVerified);
        setEnablePayment(isPaymentEnabled);
        setLiveTrackingSingleDay(isLiveTrackingEnabledSingleDay);
        setLiveTrackingMultiDay(isLiveTrackingEnabledMultiDay);
        setOpen(false);
    };

    const handleSave = () => {
        const updates = { 
            isVerified: verifyGroup, 
            isPaymentEnabled: enablePayment,
            isLiveTrackingEnabledSingleDay: liveTrackingSingleDay,
            isLiveTrackingEnabledMultiDay: liveTrackingMultiDay,
        };
        dispatch(updateGroupDetails(id, updates));
        handleClose();
    };

    const handleCreatedRidesClick = async (e) => {
        e.preventDefault();

        if (navigating) return;

        try {
            setNavigating(true);

            const groupFilter = {
                groupId: id,
                groupName: name,
                page: 0,
                sort: {
                    createdAt: "desc",
                },
            };

            dispatch(setRideFilters(groupFilter));
            navigate('/rides');
        } catch (error) {
            console.error('Error navigating to rides:', error);
        } finally {
            setNavigating(false);
        }
    };

    const alertIconStyle = {
        border: '1px solid ',
        position: 'absolute',
        left: '-28px',
        backgroundColor: '#E43700',
        color: 'white',
        borderRadius: '50%',
        fontSize: '16px',
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '8px',
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <TableList style={{ width: '100%', tableLayout: 'fixed', backgroundColor: isDeleted ? 'rgba(255, 0, 0, 0.06)' : '#ffffff' }}>
                <Td width="15%">
                    <div style={{
                        display: "flex", paddingRight: 15, alignItems: "center"
                    }}>
                        <div style={{ marginRight: 15 }}>
                            {groupPicture ? (
                                <Avatar size={35} src={groupPicture} alt="grouppicture" />
                            ) : (
                                <Avatar
                                    style={{
                                        backgroundColor: Theme.blueColor,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "14px",
                                    }}
                                    size={35}
                                >
                                    {getInitials(name)}
                                </Avatar>
                            )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {name && <Text strong>{name}</Text>}
                            {groupId && <Text type="secondary" style={{ display: "block", fontSize: "0.75rem" }}>{groupId}</Text>}
                        </div>
                    </div>
                </Td >

                <Td width="9%" style={{ verticalAlign: "left", fontSize: '0.8rem' }}>
                    {createdBy && (
                        <>
                            <Text>{`${createdBy.firstName} ${createdBy.lastName}`}</Text>
                            <Text type="secondary" style={{ display: "block", fontSize: "0.75rem", marginTop: 5, fontStyle: "italic" }}>
                                @{createdBy.userName}
                            </Text>
                        </>
                    )}
                </Td>

                <Td width="10%" style={{ verticalAlign: "left", fontSize: '0.8rem' }}>
                    {createdBy && (<Text>{`${createdBy.countryCode || '+91'} ${createdBy.phoneNumber}`}</Text>)}
                </Td>

                <Td width="8%" style={{ verticalAlign: "middle", fontSize: '0.8rem' }}>
                    {foundingPlace && <Text>{foundingPlace.displayName}</Text>}
                </Td>

                <Td width="8%" style={{ verticalAlign: "middle", fontSize: '0.8rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {createdAt && <Text>{UTCtoLocalDateTime(createdAt).date}</Text>}
                        {createdAt && <em>{UTCtoLocalDateTime(createdAt).time}</em>}
                    </div>
                </Td>

                <Td width="6%" style={{ verticalAlign: "middle", fontSize: '0.8rem' }}>
                    {memberCount && <Text>{memberCount}</Text>}
                </Td>

                <Td width="6%" style={{ verticalAlign: "middle", fontSize: '0.8rem' }}>
                    {totalKm && <Text>{totalKm}</Text>}
                </Td>


                <Td width="6%" style={{ verticalAlign: "middle", fontSize: '0.8rem' }}>
                    {totalRides && <Text>{totalRides}</Text>}
                </Td>

                <Td width="8%" style={{ verticalAlign: "middle", fontSize: '0.8rem' }}>
                    {createdRides && (
                        <Text
                            onClick={handleCreatedRidesClick}
                            style={{
                                cursor: 'pointer',
                                color: '#1890ff',
                                textDecoration: 'underline'
                            }}
                        >
                            {createdRides}
                        </Text>
                    )}
                </Td>

                <Td width="7%" style={{ verticalAlign: "middle", fontSize: '0.8rem' }}>
                    <Text>{isPrivate ? "🔒 Private" : "✅ Public"}</Text>
                </Td>

                <Td width="8%" style={{ verticalAlign: "middle" }}>
                    <Text
                        style={{
                            color: isVerified ? 'green' : 'red',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            backgroundColor: isVerified ? 'rgba(0, 128, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                            border: `1px solid ${isVerified ? 'green' : 'red'}`,
                            borderRadius: '50px',
                            padding: '5px 12px',
                            display: 'inline-block',
                        }}
                    >
                        {isVerified ? "Verified" : "Not Verified"}
                    </Text>
                </Td>

                <Td width="8%" style={{ verticalAlign: "middle" }}>
                    <Text
                        style={{
                            color: isPaymentEnabled ? 'green' : 'red',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            backgroundColor: isPaymentEnabled ? 'rgba(0, 128, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                            border: `1px solid ${isPaymentEnabled ? 'green' : 'red'}`,
                            borderRadius: '50px',
                            padding: '5px 12px',
                            display: 'inline-block',
                        }}
                    >
                        {isPaymentEnabled ? "Approved" : "Pending"}
                    </Text>
                </Td>

                <td width="3%" style={{ verticalAlign: "middle", textAlign: 'right', whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    {isDeleted && (
                        <ExclamationOutlined
                            style={alertIconStyle}
                            title="Group is deleted"
                        />
                    )}
                    <Button icon={<EyeOutlined />} onClick={handleClickOpen} />
                </div>
                    <Modal
                        title={<span
                            style={{ fontSize: '24px', fontWeight: 'bold', color: 'grey' }}>
                            Settings
                            <br />
                            <span style={{ fontSize: '14px', color: '#9c9c9c', fontStyle: 'italic' }}>
                                Group: {name && <Text style={{ color: '#9c9c9c' }}>{name}</Text>}
                            </span>
                        </span>}
                        open={open}
                        onOk={handleSave}
                        onCancel={handleClose}
                        okText="Save"
                        cancelText="Cancel"
                        width={450}
                        bodyStyle={{ padding: '20px' }}
                        footer={
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Button
                                    onClick={handleClose}
                                    style={{
                                        width: '45%',
                                        borderRadius: '25px',
                                        borderColor: 'white',
                                        color: '#6528D8',
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleSave}
                                    style={{
                                        width: '45%',
                                        borderRadius: '25px',
                                        backgroundColor: '#6528D8',
                                    }}
                                >
                                    Save
                                </Button>
                            </div>
                        }
                    >
                        <div style={{display: 'flex', flexDirection:'column'}}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px'}}>
                                <div>
                                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Verify Group</span>
                                    </label>
                                    <Paragraph type="secondary" style={{ fontSize: '0.75rem', marginBottom: 0 }}>
                                        This will make the group verified by Asteride.
                                    </Paragraph>
                                </div>
                                <div>
                                    <Switch checked={verifyGroup} onChange={() => setVerifyGroup(!verifyGroup)} />
                                </div>
                            </div>

                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px'}}>
                                <div>
                                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Enable Payment</span>
                                </label>
                                <Paragraph type="secondary" style={{ fontSize: '0.75rem', marginBottom: 0 }}>
                                    This will allow admins to make a paid ride.
                                </Paragraph>
                                </div>
                                <div>
                                    <Switch checked={enablePayment} onChange={() => setEnablePayment(!enablePayment)} />
                                </div>
                            </div>

                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px'}}>
                                <div>
                                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Live Tracking (Single-Day Rides)</span>
                                </label>
                                <Paragraph type="secondary" style={{ fontSize: '0.75rem', marginBottom: 0 }}>
                                    This will enable live tracking for single-day rides in this group.
                                </Paragraph>
                                </div>
                                <div>
                                    <Switch checked={liveTrackingSingleDay} onChange={() => setLiveTrackingSingleDay(!liveTrackingSingleDay)} />
                                </div>
                            </div>

                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px'}}>
                                <div>
                                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Live Tracking (Multi-Day Rides)</span>
                                </label>
                                <Paragraph type="secondary" style={{ fontSize: '0.75rem', marginBottom: 0 }}>
                                    This will enable live tracking for multi-day rides in this group.
                                </Paragraph>
                                </div>
                                <div>
                                    <Switch checked={liveTrackingMultiDay} onChange={() => setLiveTrackingMultiDay(!liveTrackingMultiDay)} />
                                </div>
                            </div>
                        </div>
                    </Modal>
                </td>
            </TableList >
        </div >
    );
};
